import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { listProducts } from '../actions/productActions';
import { fetchCategories } from '../actions/categoryActions';
import ProductCard from '../components/products/ProductCard';
import Loader from '../components/ui/Loader';
import Message from '../components/ui/Message';
import Paginate from '../components/ui/Paginate';
import { FaFilter, FaSearch, FaTimes } from 'react-icons/fa';

const ProductListPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get URL params with URLSearchParams
  const query = new URLSearchParams(location.search);
  const categoryParam = query.get('category') || '';
  const searchParam = query.get('search') || '';
  const sortParam = query.get('sort') || 'created_at_desc';
  const pageParam = parseInt(query.get('page') || '1', 10);
  
  const [category, setCategory] = useState(categoryParam);
  const [search, setSearch] = useState(searchParam);
  const [sort, setSort] = useState(sortParam);
  const [showFilters, setShowFilters] = useState(false);
  
  const productList = useSelector(state => state.productList);
  const { loading, error, products, pagination } = productList;
  
  const categoryList = useSelector(state => state.categoryList);
  const { categories } = categoryList;
  
  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(listProducts(searchParam, categoryParam, sortParam, pageParam));
  }, [dispatch, searchParam, categoryParam, sortParam, pageParam]);
  
  // Update state if URL params change
  useEffect(() => {
    setCategory(categoryParam);
    setSearch(searchParam);
    setSort(sortParam);
  }, [categoryParam, searchParam, sortParam]);
  
  const handleSearch = (e) => {
    e.preventDefault();
    updateFilters();
  };
  
  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    updateFilters(e.target.value, search, sort);
  };
  
  const handleSortChange = (e) => {
    setSort(e.target.value);
    updateFilters(category, search, e.target.value);
  };
  
  const clearFilters = () => {
    setCategory('');
    setSearch('');
    setSort('created_at_desc');
    navigate('/products');
  };
  
  const updateFilters = (cat = category, srch = search, srt = sort) => {
    const params = new URLSearchParams();
    
    if (cat) params.append('category', cat);
    if (srch) params.append('search', srch);
    if (srt) params.append('sort', srt);
    params.append('page', '1'); // Reset to page 1 when filters change
    
    navigate({
      pathname: '/products',
      search: params.toString()
    });
  };
  
  const getSortLabel = (sortValue) => {
    switch (sortValue) {
      case 'created_at_desc': return '最新上架';
      case 'price_asc': return '价格: 从低到高';
      case 'price_desc': return '价格: 从高到低';
      case 'name_asc': return '名称: A-Z';
      case 'name_desc': return '名称: Z-A';
      default: return '最新上架';
    }
  };
  
  // Get current category name
  const getCurrentCategoryName = () => {
    const cat = categories?.find(c => c.id === category);
    return cat ? cat.name : '所有分类';
  };
  
  // Count active filters
  const activeFiltersCount = 
    (category ? 1 : 0) + 
    (search ? 1 : 0) + 
    (sort !== 'created_at_desc' ? 1 : 0);
  
  return (
    <div className="product-list-page">
      <div className="bg-gray-50 py-8 px-4 rounded-xl mb-8">
        <div className="container mx-auto">
          <h1 className="text-4xl font-bold mb-2">中国酒品精选</h1>
          <div className="text-gray-600 flex items-center flex-wrap">
            <span>当前类别: </span>
            <span className="font-medium ml-1">{getCurrentCategoryName()}</span>
            {activeFiltersCount > 0 && (
              <button 
                onClick={clearFilters}
                className="ml-4 text-red-700 flex items-center text-sm hover:text-red-800"
              >
                <FaTimes className="mr-1" /> 
                清除全部筛选
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4">
        <div className="mb-8 flex flex-wrap items-center justify-between">
          <div className="w-full md:w-auto mb-4 md:mb-0">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white text-gray-800 px-5 py-3 rounded-lg flex items-center shadow hover:shadow-md transition-shadow"
            >
              <FaFilter className="mr-2" />
              筛选和排序
              {activeFiltersCount > 0 && (
                <span className="ml-2 bg-red-700 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
          
          <div className="w-full md:w-auto">
            <form onSubmit={handleSearch} className="flex">
              <div className="relative flex-grow">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="搜索酒品..."
                  className="w-full pl-10 pr-4 py-3 rounded-l-lg border-t border-b border-l focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent"
                />
                <div className="absolute left-3 top-3 text-gray-400">
                  <FaSearch />
                </div>
              </div>
              <button
                type="submit"
                className="bg-red-700 text-white px-6 py-3 rounded-r-lg hover:bg-red-800 transition-colors"
              >
                搜索
              </button>
            </form>
          </div>
        </div>
        
        {showFilters && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 animate-fadeIn">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">类别</label>
                <select
                  value={category}
                  onChange={handleCategoryChange}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent"
                >
                  <option value="">所有分类</option>
                  {categories && categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">排序方式</label>
                <select
                  value={sort}
                  onChange={handleSortChange}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent"
                >
                  <option value="created_at_desc">最新上架</option>
                  <option value="price_asc">价格: 从低到高</option>
                  <option value="price_desc">价格: 从高到低</option>
                  <option value="name_asc">名称: A-Z</option>
                  <option value="name_desc">名称: Z-A</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={updateFilters}
                  className="bg-red-700 text-white px-6 py-3 rounded-lg hover:bg-red-800 transition-colors w-full"
                >
                  应用筛选
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Active filters */}
        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {category && (
              <div className="bg-gray-100 rounded-full px-4 py-2 flex items-center text-sm">
                <span className="mr-2">类别: {getCurrentCategoryName()}</span>
                <button 
                  onClick={() => updateFilters('', search, sort)}
                  className="text-gray-500 hover:text-red-700"
                >
                  <FaTimes />
                </button>
              </div>
            )}
            {search && (
              <div className="bg-gray-100 rounded-full px-4 py-2 flex items-center text-sm">
                <span className="mr-2">搜索: {search}</span>
                <button 
                  onClick={() => updateFilters(category, '', sort)}
                  className="text-gray-500 hover:text-red-700"
                >
                  <FaTimes />
                </button>
              </div>
            )}
            {sort !== 'created_at_desc' && (
              <div className="bg-gray-100 rounded-full px-4 py-2 flex items-center text-sm">
                <span className="mr-2">排序: {getSortLabel(sort)}</span>
                <button 
                  onClick={() => updateFilters(category, search, 'created_at_desc')}
                  className="text-gray-500 hover:text-red-700"
                >
                  <FaTimes />
                </button>
              </div>
            )}
          </div>
        )}
        
        {loading ? (
          <div className="py-12">
            <Loader />
          </div>
        ) : error ? (
          <Message variant="error">{error}</Message>
        ) : (
          <>
            {products && products.length === 0 ? (
              <div className="py-16 text-center">
                <div className="text-5xl mb-4">🍷</div>
                <Message>没有找到商品</Message>
                <button 
                  onClick={clearFilters}
                  className="mt-4 bg-red-700 text-white px-6 py-2 rounded-lg hover:bg-red-800 transition-colors"
                >
                  清除筛选
                </button>
              </div>
            ) : (
              <>
                <div className="text-sm text-gray-600 mb-4">
                  共 {pagination?.total || 0} 款商品
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                  {products && products.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
                
                {pagination && pagination.totalPages > 1 && (
                  <div className="mt-12">
                    <Paginate
                      pages={pagination.totalPages}
                      page={pagination.page}
                      path="/products"
                      extraParams={`category=${category}&search=${search}&sort=${sort}`}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductListPage;